import React, { useState, useEffect, useRef } from "react";
import { APPLICATION_FORM_ENABLED } from "../config/featureFlags";

function Apply() {
  const [loading, setLoading] = useState(true);
  const [iframeFailed, setIframeFailed] = useState(false);
  const timeoutRef = useRef(null);
  const hasLoadedRef = useRef(false);

  // Timeout fallback in case iframe takes too long or fails silently
  useEffect(() => {
    if (!APPLICATION_FORM_ENABLED) {
      return undefined;
    }

    timeoutRef.current = setTimeout(() => {
      if (!hasLoadedRef.current) {
        setIframeFailed(true);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timeoutRef.current);
  }, []);

  // Only stop loading if iframe appears to have truly loaded
  const handleIframeLoad = () => {
    hasLoadedRef.current = true;
    setLoading(false);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const googleFormURL =
    "https://docs.google.com/forms/d/e/1FAIpQLScn2I3rnotJUPwpKLzNgUh6rSGQn3ZrIbWQJrFw-Xc5BV6DfQ/viewform?embedded=true";

  if (!APPLICATION_FORM_ENABLED) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 via-white to-emerald-50/40 px-4 pb-16 pt-8 font-[sans-serif] sm:px-6 sm:pt-12 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="overflow-hidden rounded-[28px] border border-emerald-100 bg-white shadow-[0_20px_60px_rgba(22,101,52,0.10)]">
            <div className="h-2 w-full bg-gradient-to-r from-green-900 via-emerald-600 to-lime-500" />
            <div className="px-5 py-8 sm:px-8 sm:py-10">
              <div className="mb-5 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-800">
                Waitlist Update
              </div>
              <h1 className="max-w-2xl text-3xl font-semibold tracking-tight text-green-950 sm:text-4xl">
                Applications are temporarily closed.
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-stone-700 sm:text-lg">
                Sorry, due to a large influx of waitlist applications, we are temporarily
                disabling the application form until we can work through the backlog.
                Please check back later.
              </p>
              <div className="mt-6 rounded-2xl bg-stone-50 px-4 py-4 text-sm leading-6 text-stone-700 sm:px-5">
                If you need to reach us in the meantime, email{" "}
                <a
                  href="mailto:EmmasCourtCommunityGarden@gmail.com"
                  className="font-semibold text-green-800 underline decoration-green-300 underline-offset-4 transition hover:text-green-700"
                >
                  EmmasCourtCommunityGarden@gmail.com
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8 font-[sans-serif]">

      {/* Error Message */}
      {iframeFailed && (
        <div className="w-full bg-red-200 border border-red-400 text-red-800 px-6 py-5 text-center rounded-md mb-6 shadow-md">
          <p className="text-xl font-semibold mb-1">Sorry, our site is having some problems.</p>
          <p>
            You can reach us directly at{" "}
            <a href="mailto:EmmasCourtCommunityGarden@gmail.com" className="underline font-medium">
              EmmasCourtCommunityGarden@gmail.com
            </a>
          </p>
          <button
            onClick={handleReload}
            className="mt-4 bg-green-700 text-white font-medium px-5 py-2 rounded hover:bg-green-800 transition"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Always show fallback link */}
<div className="text-sm text-black bg-white px-4 py-2 rounded-md shadow mb-6 max-w-fit mx-auto text-center">
  If the form doesn't load, you can also{" "}
  <a
    href="https://docs.google.com/forms/d/e/1FAIpQLScn2I3rnotJUPwpKLzNgUh6rSGQn3ZrIbWQJrFw-Xc5BV6DfQ/viewform"
    target="_blank"
    rel="noopener noreferrer"
    className="underline text-green-800 hover:text-green-600 font-medium"
  >
    open it in a new tab
  </a>.
  or you can reach us directly at{" "}
            <a href="mailto:EmmasCourtCommunityGarden@gmail.com" className="underline font-medium">
              EmmasCourtCommunityGarden@gmail.com
            </a>
</div>


      {/* Iframe & Spinner */}
      <div style={{ position: "relative", width: "100%", height: "854px" }}>
        {/* Spinner (fades out) */}
        <div
          className={`transition-opacity duration-700 ease-in-out ${
            loading && !iframeFailed ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          style={{
            position: "absolute",
            top: "20%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
          }}
        >
          <div className="spinner"></div>
        </div>

        {/* Iframe (fades in) */}
        {!iframeFailed && (
          <iframe
            src={googleFormURL}
            frameBorder="0"
            marginHeight="0"
            marginWidth="0"
            onLoad={handleIframeLoad}
            className={`w-full h-[854px] transition-opacity duration-700 ease-in-out ${
              loading ? "opacity-0" : "opacity-100"
            }`}
            title="Google Form"
          />
        )}
      </div>

      {/* Spinner Styling */}
      <style>{`
        .spinner {
          border: 4px solid rgba(0, 0, 0, 0.1);
          border-left-color: #000;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

export default Apply;
